import React from "react";
import {
  SortDirection,
  Table as RtTableProps,
  flexRender,
} from "@tanstack/react-table";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CircleIcon from "@mui/icons-material/Circle";
import {
  SxProps,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from "@mui/material";
import Box from "@mui/material/Box";
import { TableOwnProps } from "@mui/material";

interface TableProps<T> extends TableOwnProps {
  tableProps: RtTableProps<T>;
  containerSx?: SxProps;
  canSort?: boolean;
}

interface SortArrowProps {
  sortDirection: false | SortDirection;
}

const SortArrow: React.FC<SortArrowProps> = ({ sortDirection }) => {
  if (sortDirection === "asc") {
    return <ArrowUpwardIcon fontSize="small" sx={{ float: "right" }} />;
  }

  if (sortDirection === "desc") {
    return <ArrowDownwardIcon fontSize="small" sx={{ float: "right" }} />;
  }

  return (
    <CircleIcon
      fontSize="small"
      sx={{ float: "right", visibility: "hidden" }}
    />
  );
};

const BoxHeaderLayer = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const Table = <T extends object | unknown>({
  tableProps,
  canSort = false,
  containerSx = {},
  ...rest
}: TableProps<T>): React.JSX.Element => {
  const { getHeaderGroups, getRowModel } = tableProps;

  return (
    <TableContainer sx={containerSx}>
      <MuiTable {...rest}>
        <TableHead>
          {getHeaderGroups().map((headerElement) => (
            <TableRow key={headerElement.id}>
              {headerElement.headers.map((columnElement) => {
                const enabledSorting =
                  canSort && columnElement.column.columnDef.enableSorting;
                return (
                  <TableCell
                    colSpan={columnElement.colSpan}
                    key={columnElement.id}
                    onClick={columnElement.column.getToggleSortingHandler()}
                    sx={{ ...(enabledSorting && { cursor: "pointer" }) }}
                  >
                    <BoxHeaderLayer>
                      {flexRender(
                        columnElement.column.columnDef.header,
                        columnElement.getContext()
                      )}
                      {enabledSorting && (
                        <SortArrow
                          sortDirection={columnElement.column.getIsSorted()}
                        />
                      )}
                    </BoxHeaderLayer>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {getRowModel().rows.map((rowEl) => {
            return (
              <TableRow key={rowEl.id} selected={rowEl.getIsSelected()}>
                {rowEl.getVisibleCells().map((cellElement) => {
                  return (
                    <TableCell key={cellElement.id}>
                      {flexRender(
                        cellElement.column.columnDef.cell,
                        cellElement.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};

export default Table;
